class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.searchButton = this.input.form.querySelector('button.search__button');

    this.setupEventListeners();
  }

  setupEventListeners() {
    const form = this.input.form;
    form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    console.log(`PredictiveSearch onChange: Old term: "${this.searchTerm}", New term: "${newSearchTerm}"`);

    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    this.updateSearchForTerm(this.searchTerm, newSearchTerm);
    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }
    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    const currentQuery = this.getQuery().toLowerCase();
    console.log(`PredictiveSearch onFormSubmit: Query: "${currentQuery}"`);

    if (!currentQuery.length) {
      event.preventDefault();
      return;
    }

    const collectionLinks = this.predictiveSearchResults.querySelectorAll('li[id^="predictive-search-option-collection-"] a');
    for (const linkElement of collectionLinks) {
      const collectionTitleElement = linkElement.querySelector('.predictive-search__item-heading');
      if (collectionTitleElement) {
        const collectionTitle = collectionTitleElement.textContent.trim().toLowerCase();
        if (collectionTitle === currentQuery) {
          console.log(`PredictiveSearch onFormSubmit: Matched collection "${collectionTitle}". Navigating to ${linkElement.href}`);
          event.preventDefault();
          window.location.href = linkElement.href;
          return;
        }
      }
    }
    if (this.querySelector('[aria-selected="true"] a')) {
      // This case should ideally be handled by selectOption, but as a fallback:
      // event.preventDefault();
    }
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();
    console.log(`PredictiveSearch onFocus: Current term: "${currentSearchTerm}", Stored searchTerm: "${this.searchTerm}"`);
    if (!currentSearchTerm.length) return;
    if (this.searchTerm !== currentSearchTerm) {
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length && event.code !== 'Escape') this.close(true);

    switch (event.code) {
      case 'ArrowUp':
        event.preventDefault();
        this.switchOption('up');
        break;
      case 'ArrowDown':
        event.preventDefault();
        this.switchOption('down');
        break;
      case 'Enter':
        event.preventDefault();
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
    if (event.code === 'Escape') {
      this.close();
    }
  }

  escapeRegex(string) {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    if (!searchForTextElement) return;

    const newT = newTerm || '';
    let baseTextWithPlaceholder = 'Search for \"{terms}\"'; // Default English text with {terms} placeholder

    // Safely attempt to get the translation string
    if (typeof Shopify === 'object' && Shopify !== null &&
        typeof Shopify.translationCache === 'object' && Shopify.translationCache !== null &&
        typeof Shopify.translationCache.get === 'function'
    ) {
      try {
        const cachedText = Shopify.translationCache.get('templates.search.search_for');
        if (typeof cachedText === 'string' && cachedText.includes('{terms}')) {
          baseTextWithPlaceholder = cachedText;
        } else if (typeof cachedText === 'string') {
          console.warn('PredictiveSearch: Translation for templates.search.search_for found but lacks {terms} placeholder. Using default.');
        }
      } catch (e) {
        console.warn('PredictiveSearch: Error accessing Shopify.translationCache.get. Using default.', e);
      }
    }

    searchForTextElement.innerText = baseTextWithPlaceholder.replace('{terms}', newT);
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;
    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    if (allVisibleElements.length === 0) return;

    let activeElementIndex = 0;
    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    if (selectedElement) {
      selectedElementIndex = allVisibleElements.indexOf(selectedElement);
    }

    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    if (this.statusElement) this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
      if (selectedElementIndex === -1) activeElementIndex = allVisibleElements.length - 1;
    } else if (!selectedElement && !moveUp) {
      activeElementIndex = 0;
    }

    if (activeElementIndex === selectedElementIndex && selectedElement) return;

    const activeElement = allVisibleElements[activeElementIndex];
    if (activeElement) activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement ? activeElement.id : '');
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"].predictive-search__item--term');
    if (selectedOption) {
      selectedOption.click();
    }
  }

  getSearchResults(searchTerm) {
    console.log(`PredictiveSearch getSearchResults: Fetching for term: "${searchTerm}"`);
    this.abortController.abort();
    this.abortController = new AbortController();

    const queryKey = searchTerm.replace(/s+/g, '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&resources[type]=product,collection,query,page,article&resources[limit]=10&section_id=predictive-search`, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          const error = new Error(`HTTP error! status: ${response.status}`);
          error.status = response.status;
          console.error('Error fetching predictive search results:', error);
          this.close();
          this.removeAttribute('loading');
          return null;
        }
        return response.text();
      })
      .then((text) => {
        if (text === null) return;
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.log('Predictive search fetch aborted (likely due to new input)');
          return;
        }
        console.error('Failed to get predictive search results:', error);
        this.close();
        this.removeAttribute('loading');
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');
    if (!this.statusElement || !this.loadingText) return;

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    if (!this.statusElement) return;
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      if (this.statusElement) this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);
    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    const countValueElement = this.querySelector('[data-predictive-search-live-region-count-value]');
    if (this.statusElement && countValueElement) {
      this.setLiveRegionText(countValueElement.textContent);
    }
  }

  getResultsMaxHeight() {
    try {
      const headerElement = document.querySelector('.section-header');
      if (headerElement) {
        this.resultsMaxHeight = window.innerHeight - headerElement.getBoundingClientRect().bottom;
      } else {
        console.warn('PredictiveSearch: .section-header not found, using fallback max height.');
        this.resultsMaxHeight = 300;
      }
    } catch (e) {
      console.error('PredictiveSearch: Error calculating max height:', e);
      this.resultsMaxHeight = 300;
    }
    return this.resultsMaxHeight;
  }

  open() {
    const maxHeight = this.resultsMaxHeight !== false ? this.resultsMaxHeight : this.getResultsMaxHeight();
    this.predictiveSearchResults.style.maxHeight = `${maxHeight}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.searchTerm = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');
    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);
